// ============================================================
// FEAT-05: src/pages/RelatorioGMD.js — Relatório de GMD dedicado
// Criar em /opt/ovinogest/frontend/src/pages/RelatorioGMD.js
// Adicionar rota em App.js: <Route path="/relatorios/gmd" element={<RelatorioGMD />} />
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Download, Filter, Search, BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';
import { useConfig } from '../context/ConfigContext';
import { limiaresGmd, CORES_GMD } from '../utils/gmd';

const RelatorioGMD = () => {
  const { config } = useConfig();
  const [dados, setDados] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    lote_id: '',
    raca_id: '',
    categoria: '',
    periodo_inicio: '',
    periodo_fim: '',
  });
  const [lotes, setLotes] = useState([]);
  const [racas, setRacas] = useState([]);
  const [visualizacao, setVisualizacao] = useState('tabela'); // tabela | grafico_individual | grafico_lote

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.append(k, v); });

      const [gmdRes, lotesRes, racasRes] = await Promise.all([
        api.get(`/relatorios/gmd?${params.toString()}`),
        api.get('/lotes'),
        api.get('/racas'),
      ]);

      setDados((gmdRes.data.animais || gmdRes.data || []).map(a => ({ ...a, gmd: a.gmd ? parseFloat(a.gmd) * 1000 : null })));
      const r = gmdRes.data.resumo;
      if (r) {
        setResumo({ ...r, gmd_medio: r.gmd_medio * 1000, gmd_maximo: r.gmd_maximo * 1000, gmd_minimo: r.gmd_minimo * 1000 });
      }
      setLotes(lotesRes.data || []);
      setRacas(racasRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar GMD:', err);
      toast.error('Erro ao carregar relatório de GMD');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // ── Exportar CSV ──
  const exportarCSV = () => {
    if (dados.length === 0) return toast.error('Nenhum dado para exportar');
    const headers = ['Brinco', 'Nome', 'Sexo', 'Raça', 'Lote', 'Categoria', 'Nascimento', 'Idade (dias)',
      'Peso Referência (kg)', 'Peso Atual (kg)', 'GMD Acumulado (g/dia)', 'Última Pesagem'];
    const rows = dados.map(a => [
      a.brinco || '', a.nome || '', a.sexo === 'F' ? 'Fêmea' : 'Macho', a.raca_nome || '', a.lote_nome || '',
      a.categoria || '', a.data_nascimento ? formatDateBR(a.data_nascimento) : '', a.idade_dias || '',
      a.peso_referencia != null ? `${parseFloat(a.peso_referencia).toFixed(1)}${a.referencia_origem === 'estimado' ? ' (estimado)' : ''}` : '',
      a.peso_atual || '',
      a.gmd != null ? a.gmd.toFixed(0) : '',
      a.ultima_pesagem ? formatDateBR(a.ultima_pesagem) : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_gmd_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado!');
  };

  // ── GMD cor por faixa — mesmos limiares configuráveis (g/d) da tela de Pesagens ──
  const { verdeMin, amareloMin } = limiaresGmd(config);
  const getGMDCor = (gmdG) => {
    if (gmdG == null) return { ...CORES_GMD.vermelho, label: '—' };
    const nivel = gmdG >= verdeMin ? 'verde' : gmdG >= amareloMin ? 'amarelo' : 'vermelho';
    return { ...CORES_GMD[nivel], label: CORES_GMD[nivel].label };
  };

  // ── Dados para gráfico por lote ──
  const dadosPorLote = () => {
    const agrupado = {};
    dados.forEach(a => {
      const lote = a.lote_nome || 'Sem lote';
      if (!agrupado[lote]) agrupado[lote] = { lote, somaGmd: 0, count: 0 };
      if (a.gmd) {
        agrupado[lote].somaGmd += a.gmd;
        agrupado[lote].count++;
      }
    });
    return Object.values(agrupado).map(g => ({
      lote: g.lote,
      gmd_medio: g.count > 0 ? Math.round(g.somaGmd / g.count) : 0,
      animais: g.count,
    })).sort((a, b) => b.gmd_medio - a.gmd_medio);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <TrendingUp size={24} />
          <h1>Relatório de GMD</h1>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportarCSV}>
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Resumo */}
      {resumo && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          <div className="card" style={{ padding: '14px', textAlign: 'center', borderTop: '3px solid #2563eb' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>{resumo.gmd_medio?.toFixed(0) || 0}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>GMD Médio (g/dia)</div>
          </div>
          <div className="card" style={{ padding: '14px', textAlign: 'center', borderTop: '3px solid #16a34a' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{resumo.gmd_maximo?.toFixed(0) || 0}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>GMD Máximo (g/dia)</div>
          </div>
          <div className="card" style={{ padding: '14px', textAlign: 'center', borderTop: '3px solid #dc2626' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{resumo.gmd_minimo?.toFixed(0) || 0}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>GMD Mínimo (g/dia)</div>
          </div>
          <div className="card" style={{ padding: '14px', textAlign: 'center', borderTop: '3px solid #6b7280' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6b7280' }}>{resumo.total_animais || dados.length}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Animais Avaliados</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select className="input" style={{ width: '180px' }} value={filtros.lote_id}
          onChange={e => setFiltros(p => ({ ...p, lote_id: e.target.value }))}>
          <option value="">Todos os lotes</option>
          {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
        <select className="input" style={{ width: '180px' }} value={filtros.raca_id}
          onChange={e => setFiltros(p => ({ ...p, raca_id: e.target.value }))}>
          <option value="">Todas as raças</option>
          {racas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
        </select>
        <select className="input" style={{ width: '180px' }} value={filtros.categoria}
          onChange={e => setFiltros(p => ({ ...p, categoria: e.target.value }))}>
          <option value="">Todas categorias</option>
          <option value="cordeiro">Cordeiro</option>
          <option value="borrego">Borrego</option>
          <option value="reprodutor">Reprodutor</option>
          <option value="matriz">Matriz</option>
        </select>
        <input type="date" className="input" style={{ width: '160px' }} value={filtros.periodo_inicio}
          onChange={e => setFiltros(p => ({ ...p, periodo_inicio: e.target.value }))} placeholder="De" />
        <input type="date" className="input" style={{ width: '160px' }} value={filtros.periodo_fim}
          onChange={e => setFiltros(p => ({ ...p, periodo_fim: e.target.value }))} placeholder="Até" />
      </div>

      {/* Abas de visualização */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'tabela', label: 'Tabela', icon: <BarChart3 size={14} /> },
          { key: 'grafico_lote', label: 'Gráfico por Lote', icon: <BarChart3 size={14} /> },
        ].map(aba => (
          <button key={aba.key} onClick={() => setVisualizacao(aba.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '8px 16px', border: 'none', cursor: 'pointer', background: 'none',
              borderBottom: visualizacao === aba.key ? '2px solid var(--primary, #2D6A4F)' : '2px solid transparent',
              color: visualizacao === aba.key ? 'var(--primary, #2D6A4F)' : '#6b7280',
              fontWeight: visualizacao === aba.key ? 'bold' : 'normal', fontSize: '13px',
            }}>
            {aba.icon} {aba.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner"></div><p>Carregando...</p></div>
      ) : dados.length === 0 ? (
        <div className="empty-state">
          <TrendingUp size={48} />
          <h3>Nenhum dado de GMD</h3>
          <p>Registre pesagens para os animais verem o GMD calculado aqui.</p>
        </div>
      ) : visualizacao === 'tabela' ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Brinco</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Nome</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Sexo</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Raça</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Lote</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Categoria</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Idade</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Peso Ref. (kg)</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Peso Atual (kg)</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Última Pesagem</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>GMD Acumulado (g/dia)</th>
                <th style={{ padding: '10px', textAlign: 'center' }}>Classificação</th>
              </tr>
            </thead>
            <tbody>
              {dados.sort((a, b) => (b.gmd ?? -Infinity) - (a.gmd ?? -Infinity)).map((a, i) => {
                const gmdInfo = getGMDCor(a.gmd);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px', fontWeight: '600' }}>{a.brinco || '-'}</td>
                    <td style={{ padding: '10px' }}>{a.nome || '-'}</td>
                    <td style={{ padding: '10px' }}>{a.sexo === 'F' ? 'Fêmea' : 'Macho'}</td>
                    <td style={{ padding: '10px' }}>{a.raca_nome || '-'}</td>
                    <td style={{ padding: '10px' }}>{a.lote_nome || '-'}</td>
                    <td style={{ padding: '10px', textTransform: 'capitalize' }}>{a.categoria || '-'}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{a.idade_dias || '-'} d</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }} title={a.referencia_origem === 'estimado' ? 'Estimado (sem pesagem ≤30d de vida)' : 'Pesagem real ≤30d de vida'}>
                      {a.peso_referencia != null ? `${parseFloat(a.peso_referencia).toFixed(1)}${a.referencia_origem === 'estimado' ? '*' : ''}` : '-'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{a.peso_atual ? `${a.peso_atual} kg` : '-'}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }}>{a.ultima_pesagem ? formatDateBR(a.ultima_pesagem) : '-'}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: gmdInfo.color }}>
                      {a.gmd != null ? a.gmd.toFixed(0) : '—'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: gmdInfo.bg, color: gmdInfo.color,
                        padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {gmdInfo.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* Gráfico por Lote */
        <div style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dadosPorLote()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="lote" angle={-30} textAnchor="end" fontSize={12} />
              <YAxis label={{ value: 'GMD Médio (g/dia)', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={(value, name) => [
                  name === 'gmd_medio' ? `${value} g/dia` : value,
                  name === 'gmd_medio' ? 'GMD Médio' : 'Animais'
                ]}
              />
              <Legend />
              <Bar dataKey="gmd_medio" name="GMD Médio (g/dia)" fill="var(--primary, #2D6A4F)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Legenda — mesmos limiares parametrizáveis (Configurações) usados em Pesagens */}
      <div style={{ marginTop: '20px', padding: '14px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <strong style={{ fontSize: '13px' }}>Critérios de GMD Acumulado:</strong>
        <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '12px', flexWrap: 'wrap' }}>
          <span><span style={{ color: CORES_GMD.verde.color, fontWeight: 'bold' }}>🟢 Verde:</span> ≥ {verdeMin} g/dia</span>
          <span><span style={{ color: CORES_GMD.amarelo.color, fontWeight: 'bold' }}>🟡 Amarelo:</span> ≥ {amareloMin} e &lt; {verdeMin} g/dia</span>
          <span><span style={{ color: CORES_GMD.vermelho.color, fontWeight: 'bold' }}>🔴 Vermelho:</span> &lt; {amareloMin} g/dia</span>
          <span style={{ color: '#6b7280' }}>(valores ajustáveis em Configurações)</span>
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
          * Peso de referência estimado (3,0 kg) — o animal não tem pesagem registrada com até 30 dias de vida.
        </div>
        <div style={{ marginTop: 2, fontSize: 11, color: '#9ca3af' }}>
          GMD Acumulado é calculado para animais de qualquer idade (desde o início do acompanhamento).
        </div>
      </div>
    </div>
  );
};

export default RelatorioGMD;
